import { useCallback, useRef, useState } from "react";

/* Types */
export type CanvasTool = "pan" | "draw-rect" | "draw-polygon";

/*
 * useCanvasDrawingState
 * Keeps drawing-mode state and draft-shape state together so the canvas component
 * can focus on pointer interactions and rendering.
 */
export const useCanvasDrawingState = () => {
  /* State */
  const [toolMode, setToolMode] = useState<CanvasTool>("pan");
  const [isDrawing, setIsDrawing] = useState(false);
  const [draftRect, setDraftRect] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [draftPolygonPoints, setDraftPolygonPoints] = useState<{ x: number; y: number }[]>([]);
  const [polygonCursorPoint, setPolygonCursorPoint] = useState<{ x: number; y: number } | null>(null);

  /* Refs */
  const drawStartRef = useRef<{ x: number; y: number } | null>(null);

  /* Helpers */
  const clearRectDraft = useCallback(() => {
    // End any active rectangle draw and clear the temporary preview box
    setIsDrawing(false);
    setDraftRect(null);
    drawStartRef.current = null;
  }, []);

  const clearPolygonDraft = useCallback(() => {
    // Remove the in-progress polygon vertices and cursor preview point
    setDraftPolygonPoints([]);
    setPolygonCursorPoint(null);
  }, []);

  /* Handlers */
  const handleToolSwitch = useCallback(
    (tool: CanvasTool) => {
      // Switch the active tool first so the UI updates immediately
      setToolMode(tool);

      // Leaving polygon mode should discard its draft state
      if (tool !== "draw-polygon") {
        clearPolygonDraft();
      }

      // Leaving rectangle mode should discard its draft state
      if (tool !== "draw-rect") {
        clearRectDraft();
      }

      // Entering rectangle mode should always start from a fresh non-drawing state
      if (tool === "draw-rect") {
        setIsDrawing(false);
      }
    },
    [clearPolygonDraft, clearRectDraft],
  );

  const cancelActiveDrawing = useCallback(() => {
    // Escape/cancel should only clear the draft state for the currently active tool
    if (toolMode === "draw-polygon") {
      clearPolygonDraft();
    }

    if (toolMode === "draw-rect") {
      clearRectDraft();
    }
  }, [clearPolygonDraft, clearRectDraft, toolMode]);

  return {
    toolMode,
    isDrawing,
    draftRect,
    draftPolygonPoints,
    polygonCursorPoint,
    drawStartRef,
    setToolMode,
    setIsDrawing,
    setDraftRect,
    setDraftPolygonPoints,
    setPolygonCursorPoint,
    handleToolSwitch,
    cancelActiveDrawing,
    clearPolygonDraft,
    clearRectDraft,
  };
};
